import { useEffect, useRef, useState } from 'react';
import { useObserver } from 'mobx-react-lite';
import useCart from 'src/hooks/use-cart';
import { useApolloClient } from '@apollo/client';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import type { Dispensaries } from 'src/gql/graphql';
import useUser from 'src/hooks/use-user';
import useErnie from 'shared/hooks/use-ernie';
import { useRouter } from 'next/compat/router';
import { useLoyaltyEnrollmentAnalytics } from 'src/hooks/use-loyalty-enrollment-analytics';
import type { LoyaltyEnrollmentIntegration } from 'src/utils/analytics/events';
import {
  Statuses,
  Integrations,
  getEnrollmentRequiresPhoneVerification,
  getIsDutchiePosLoyaltyEnrollmentEligible,
  getVerificationRequiredPrompt,
  verificationRequiredPrompts,
} from './types';
import type {
  UseLoyaltyProps,
  UseLoyaltyReturn,
  EnrollmentRequiresPhoneVerificationInput,
  DutchiePosLoyaltyEnrollmentEligibilityInput,
} from './types';
import { createAlpineLoyalty } from './use-alpine-loyalty';
import { createSpringbigLoyalty } from './use-springbig-loyalty';
import { createDutchiePosLoyalty } from './use-dutchie-pos-loyalty';
import { createNoLoyalty } from './no-loyalty';

export {
  Statuses,
  Integrations,
  getEnrollmentRequiresPhoneVerification,
  getIsDutchiePosLoyaltyEnrollmentEligible,
  getVerificationRequiredPrompt,
  verificationRequiredPrompts,
};
export type { EnrollmentRequiresPhoneVerificationInput, DutchiePosLoyaltyEnrollmentEligibilityInput };

export const useLoyalty = ({
  initialStatus,
  integration,
  surface,
  referralData = null,
}: UseLoyaltyProps): UseLoyaltyReturn => {
  const [status, setStatus] = useState(initialStatus);
  const { dispensary } = useDispensary();
  const Cart = useCart();
  const User = useUser();
  const router = useRouter();
  const apolloClient = useApolloClient();
  const showErnie = useErnie();

  const enrollmentIntegration: LoyaltyEnrollmentIntegration | undefined =
    integration === Integrations.none ? undefined : integration;
  const analytics = useLoyaltyEnrollmentAnalytics({ surface, integration: enrollmentIntegration });

  const { trackViewed } = analytics;
  const hasRecordedRef = useRef(false);
  useEffect(() => {
    if (status !== Statuses.signup || surface === null) {
      hasRecordedRef.current = false;
      return;
    }
    if (!hasRecordedRef.current && enrollmentIntegration !== undefined) {
      trackViewed();
      hasRecordedRef.current = true;
    }
  }, [status, surface, enrollmentIntegration, trackViewed]);

  const phone = useObserver(() => User.phone);
  const birthday = useObserver(() => User.birthday);
  const email = useObserver(() => User.email);
  const firstName = useObserver(() => User.firstName);
  const lastName = useObserver(() => User.lastName);

  const alpineDeps = {
    dispensary,
    cart: Cart,
    user: {
      phone,
      birthday,
      email,
      firstName,
      lastName,
      setLoyaltyPin: User.setLoyaltyPin,
      hasLoyaltyAccount: User.hasLoyaltyAccount,
    },
    apolloClient,
    showErnie,
    analytics,
  };

  const springbigDeps = {
    dispensary,
    cart: Cart,
    user: {
      id: User.id,
      phone,
      birthday,
    },
    apolloClient,
    showErnie,
    analytics,
  };

  const dposDeps = {
    dispensary,
    cart: Cart,
    user: {
      id: User.id,
      phone,
      birthday,
      email,
      firstName,
      lastName,
    },
    apolloClient,
    showErnie,
    analytics,
  };

  switch (integration) {
    case Integrations.alpineiq:
      return createAlpineLoyalty({ status, setStatus, deps: alpineDeps });
    case Integrations.dutchiepos:
      return createDutchiePosLoyalty({ status, setStatus, deps: dposDeps, router, referralData });
    case Integrations.springbig:
      return createSpringbigLoyalty({ status, setStatus, deps: springbigDeps });
    case Integrations.none:
      return createNoLoyalty({ status, setStatus });
  }
  return createNoLoyalty({ status, setStatus });
};

export const detectLoyaltyIntegration = (dispensary: Dispensaries | undefined): Integrations => {
  if (!dispensary) {
    return Integrations.none;
  }

  const alpineEnabled = dispensary.consumerDispensaryIntegrations?.alpineiq?.enabled;
  const springbigEnabled = dispensary.consumerDispensaryIntegrations?.springbig?.enabled;
  const dutchieLoyaltyEnable = dispensary.consumerDispensaryIntegrations?.dutchieposloyalty?.enabled;

  if (alpineEnabled) {
    return Integrations.alpineiq;
  }
  if (springbigEnabled) {
    return Integrations.springbig;
  }
  if (dutchieLoyaltyEnable) {
    return Integrations.dutchiepos;
  }

  return Integrations.none;
};
